import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, IconButton, Stack, Typography } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIosRounded';
import MemoryOutlinedIcon from '@mui/icons-material/MemoryOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

const AUTOPLAY_MS = 6000;

const slides = [
  {
    icon: <MemoryOutlinedIcon sx={{ fontSize: { xs: 160, md: 260 } }} />,
    eyebrow: 'Catálogo',
    title: 'Arma, repara y potencia tu PC',
    description: 'Componentes originales para gaming, oficina y trabajo profesional, con stock real.',
    primaryCta: { label: 'Ver catálogo', to: '/catalogo' },
    secondaryCta: { label: 'Cotizar mi PC', to: '/cotizador' },
    gradient: 'linear-gradient(135deg, #103996 0%, #2065D1 100%)',
  },
  {
    icon: <RequestQuoteOutlinedIcon sx={{ fontSize: { xs: 160, md: 260 } }} />,
    eyebrow: 'Cotizador',
    title: 'Arma tu equipo ideal en minutos',
    description: 'Elige tus componentes y obtén tu cotización al instante, en soles y dólares.',
    primaryCta: { label: 'Cotizar ahora', to: '/cotizador' },
    secondaryCta: { label: 'Ver catálogo', to: '/catalogo' },
    gradient: 'linear-gradient(135deg, #27097A 0%, #8E33FF 100%)',
  },
  {
    icon: <LocalShippingOutlinedIcon sx={{ fontSize: { xs: 160, md: 260 } }} />,
    eyebrow: 'Checkout',
    title: 'Compra como invitado o con tu cuenta',
    description: 'Sin registro obligatorio: paga y recibe tu boleta o factura al instante.',
    primaryCta: { label: 'Ir al catálogo', to: '/catalogo' },
    secondaryCta: { label: 'Ver mi carrito', to: '/carrito' },
    gradient: 'linear-gradient(135deg, #04297A 0%, #1890FF 100%)',
  },
  {
    icon: <BuildOutlinedIcon sx={{ fontSize: { xs: 160, md: 260 } }} />,
    eyebrow: 'Servicio técnico',
    title: 'Mantenimiento, reparación y soporte',
    description: 'Solicita el servicio que necesitas y te contactamos para coordinar la recepción.',
    primaryCta: { label: 'Solicitar servicio', to: '/servicio-tecnico' },
    secondaryCta: { label: 'Ver catálogo', to: '/catalogo' },
    gradient: 'linear-gradient(135deg, #7A0C2E 0%, #FF4842 100%)',
  },
];

// Hero tipo slider: rota automaticamente entre "banners" con degradado + icono grande
// (no hay fotografia de producto en el catalogo), con flechas y puntos de navegacion.
// Se pausa mientras el puntero esta encima.
export default function HeroSlider() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timeoutRef = useRef(null);

  const goTo = useCallback((index) => {
    setActive((index + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (paused) {
      return undefined;
    }

    timeoutRef.current = setTimeout(() => goTo(active + 1), AUTOPLAY_MS);
    return () => clearTimeout(timeoutRef.current);
  }, [active, paused, goTo]);

  return (
    <Box
      sx={{ position: 'relative', overflow: 'hidden', color: 'common.white' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, index) => (
        <Box
          key={slide.title}
          sx={{
            position: index === active ? 'relative' : 'absolute',
            inset: 0,
            opacity: index === active ? 1 : 0,
            transition: 'opacity 0.6s ease-in-out',
            background: slide.gradient,
            pointerEvents: index === active ? 'auto' : 'none',
          }}
          aria-hidden={index !== active}
        >
          <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
            <Stack sx={{ alignItems: 'center', justifyContent: 'space-between' }}
              direction={{ xs: 'column', md: 'row' }}
              spacing={4}
            >
              <Stack spacing={2.5} sx={{ maxWidth: 560, textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.8 }}>
                  {slide.eyebrow}
                </Typography>
                <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 46 }, fontWeight: 700 }}>
                  {slide.title}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {slide.description}
                </Typography>
                <Stack sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    component={RouterLink}
                    to={slide.primaryCta.to}
                    variant="contained"
                    color="inherit"
                    size="large"
                    sx={{ color: 'grey.900' }}
                  >
                    {slide.primaryCta.label}
                  </Button>
                  <Button
                    component={RouterLink}
                    to={slide.secondaryCta.to}
                    variant="outlined"
                    color="inherit"
                    size="large"
                    sx={{ borderColor: 'rgba(255,255,255,0.5)' }}
                  >
                    {slide.secondaryCta.label}
                  </Button>
                </Stack>
              </Stack>

              <Box sx={{ display: { xs: 'none', md: 'block' }, opacity: 0.25 }}>{slide.icon}</Box>
            </Stack>
          </Container>
        </Box>
      ))}

      {/* Flechas de navegacion */}
      <IconButton
        onClick={() => goTo(active - 1)}
        aria-label="Anterior"
        sx={{
          position: 'absolute',
          top: '50%',
          left: { xs: 8, md: 24 },
          transform: 'translateY(-50%)',
          color: 'common.white',
          bgcolor: 'rgba(255,255,255,0.15)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
        }}
      >
        <ArrowBackIosNewIcon fontSize="small" />
      </IconButton>
      <IconButton
        onClick={() => goTo(active + 1)}
        aria-label="Siguiente"
        sx={{
          position: 'absolute',
          top: '50%',
          right: { xs: 8, md: 24 },
          transform: 'translateY(-50%)',
          color: 'common.white',
          bgcolor: 'rgba(255,255,255,0.15)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
        }}
      >
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>

      {/* Puntos indicadores */}
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'center', position: 'absolute', bottom: 16, left: 0, right: 0 }}>
        {slides.map((slide, index) => (
          <Box
            key={slide.title}
            component="button"
            type="button"
            onClick={() => goTo(index)}
            aria-label={`Ir al banner ${index + 1}`}
            sx={{
              width: index === active ? 24 : 8,
              height: 8,
              borderRadius: 4,
              border: 'none',
              cursor: 'pointer',
              bgcolor: index === active ? 'common.white' : 'rgba(255,255,255,0.5)',
              transition: 'width 0.3s ease',
              p: 0,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}
